import React, { useState, useContext, useCallback, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import {
  Tabs,
  Tab,
  Typography,
  Box,
  Stack,
  Container,
  Stepper,
  Step,
  StepLabel,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import DoneAllIcon from '@mui/icons-material/DoneAll';

import FileInput from '../components/file-input/index';
import CreateEnvelope from '../components/create-envelope/index';

import { EntityContext } from '../entity';

import { GroupList } from '../components/signer-input';

// ----------------------------------------------------------------------

const steps = ['Envelope', 'Documentos', 'Grupo de Assinantes', 'Fim'];

const NEW_ENVELOPE_DRAFT_KEY = 'newEnvelopeDraft';

function getInitialJourneyState() {
  try {
    const raw = sessionStorage.getItem(NEW_ENVELOPE_DRAFT_KEY);
    if (!raw) return { value: 0, envelopeKey: '', maxUnlockedStep: 0 };
    const draft = JSON.parse(raw);
    if (draft.completed === true) return { value: 0, envelopeKey: '', maxUnlockedStep: 0 };
    const envelopeKey = draft.envelopeKey || '';
    if (!envelopeKey) return { value: 0, envelopeKey: '', maxUnlockedStep: 0 };

    let value = Number.isFinite(draft.value) ? draft.value : 0;
    let maxUnlockedStep = Number.isFinite(draft.maxUnlockedStep) ? draft.maxUnlockedStep : value;
    value = Math.min(Math.max(0, value), 2);
    maxUnlockedStep = Math.min(Math.max(maxUnlockedStep, value, 1), 2);
    return { value, envelopeKey, maxUnlockedStep };
  } catch {
    return { value: 0, envelopeKey: '', maxUnlockedStep: 0 };
  }
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function NewEnvelope() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const shouldResumeDraft = Boolean(location.state?.resume);

  const boot = useMemo(
    () => (shouldResumeDraft ? getInitialJourneyState() : { value: 0, envelopeKey: '', maxUnlockedStep: 0 }),
    [shouldResumeDraft]
  );

  const { createEnvelope } = useContext(EntityContext);
  const [value, setValue] = useState(boot.value);
  const [maxUnlockedStep, setMaxUnlockedStep] = useState(boot.maxUnlockedStep);
  const [envelopeKey, setEnvelopeKey] = useState(boot.envelopeKey);
  const [done, setDone] = useState(false);

  const changeStep = useCallback((newStep) => {
    setValue(newStep);
    setMaxUnlockedStep((prev) => Math.max(prev, newStep));
  }, []);

  const completeJourney = useCallback(() => {
    setDone(true);
    setValue(3);
    setMaxUnlockedStep((prev) => Math.max(prev, 3));
    sessionStorage.removeItem(NEW_ENVELOPE_DRAFT_KEY);
  }, []);

  useEffect(() => {
    if (!shouldResumeDraft) {
      sessionStorage.removeItem(NEW_ENVELOPE_DRAFT_KEY);
    }
  }, [shouldResumeDraft]);

  useEffect(() => {
    if (done) return;
    const persistValue = Math.min(value, 2);
    const persistMax = Math.min(maxUnlockedStep, 2);
    if (envelopeKey || persistValue > 0) {
      sessionStorage.setItem(
        NEW_ENVELOPE_DRAFT_KEY,
        JSON.stringify({
          envelopeKey,
          value: persistValue,
          maxUnlockedStep: Math.max(persistMax, envelopeKey ? 1 : 0, persistValue),
        })
      );
    } else {
      sessionStorage.removeItem(NEW_ENVELOPE_DRAFT_KEY);
    }
  }, [envelopeKey, value, maxUnlockedStep, done]);

  const handleChange = (event, newValue) => {
    if (done) return;
    if (newValue === 3) return;
    if (newValue > maxUnlockedStep) return;
    setValue(newValue);
  };

  const postEnvelope = useCallback(
    async (name) => {
      const payload = {
        envelope_name: name,
      };

      try {
        createEnvelope(payload).then((data) => {
          if (data.error) {
            changeStep(0);
          } else {
            setEnvelopeKey(data.envelope_key);
            changeStep(1);
          }
        });
      } catch (error) {
        console.log(error);
      }
    },
    [createEnvelope, changeStep]
  );

  const tabDisabled = (index) => done || index > maxUnlockedStep || index === 3;

  return (
    <>
      <Helmet>
        <title> Novo Envelope </title>
      </Helmet>

      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1, sm: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          {isMobile ? (
            <Box sx={{ mb: 3 }}>
              <Stepper activeStep={value} alternativeLabel>
                {steps.map((label, index) => (
                  <Step
                    key={label}
                    completed={value > index}
                    disabled={done ? index !== value : index > maxUnlockedStep}
                  >
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          ) : (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
                disabled={done}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    minWidth: { xs: 80, sm: 120 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  },
                }}
              >
                <Tab label="Envelope" {...a11yProps(0)} disabled={tabDisabled(0)} />
                <Tab label="Documentos" {...a11yProps(1)} disabled={tabDisabled(1)} />
                <Tab label="Grupo de Assinantes" {...a11yProps(2)} disabled={tabDisabled(2)} />
                <Tab label="Fim" {...a11yProps(3)} disabled={tabDisabled(3)} />
              </Tabs>
            </Box>
          )}

          <TabPanel value={value} index={0}>
            <CreateEnvelope
              changeStep={changeStep}
              createEnvelope={postEnvelope}
              envelopeKey={envelopeKey}
            />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <FileInput changeStep={changeStep} envelopeKey={envelopeKey} />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <GroupList
              changeStep={changeStep}
              envelopeKey={envelopeKey}
              onEnvelopeSent={completeJourney}
            />
          </TabPanel>
          <TabPanel value={value} index={3}>
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={3}
              sx={{
                py: { xs: 4, sm: 8 },
                minHeight: { xs: 300, sm: 400 },
              }}
            >
              <DoneAllIcon color="success" sx={{ fontSize: { xs: 60, sm: 100 } }} />
              <Typography variant="h5" align="center">
                Envelope enviado para assinatura!
              </Typography>
            </Stack>
          </TabPanel>
        </Paper>
      </Container>
    </>
  );
}
